import React, { useState } from 'react';
import { TimeSlotSelector } from './TimeSlotSelector';
import { ToggleSwitch } from './ToggleSwitch';
import {
  isValidTimeSlot,
  hasTimeOverlap,
  isOvernightTime,
  format12Hour,
  addMinutesToTime,
} from '../../utils/timeValidation';

// -------------------------
// Types
// -------------------------
interface TimeSlot {
  start: string;
  end: string;
}

interface DaySchedule {
  firstHalf: TimeSlot;
  secondHalf: TimeSlot;
  isClosed: boolean;
}

type OperatingHours = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  DaySchedule
>;

interface DayCustomizerProps {
  operatingHours: OperatingHours;
  onChange: (hours: OperatingHours) => void;
  isExpanded?: boolean;
}

const DAYS: Array<keyof OperatingHours> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// -------------------------
// DayCustomizer Component
// -------------------------
export const DayCustomizer: React.FC<DayCustomizerProps> = ({
  operatingHours,
  onChange,
  isExpanded: initialExpanded = true,
}) => {
  const [selectedDay, setSelectedDay] = useState<keyof OperatingHours | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<DaySchedule | null>(null);
  const [hasSecondPeriod, setHasSecondPeriod] = useState(false);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  // -------------------------
  // Helper Functions
  // -------------------------
  const getPreviousDay = (day: keyof OperatingHours): keyof OperatingHours => {
    const index = DAYS.indexOf(day);
    return DAYS[(index - 1 + 7) % 7];
  };

  const getNextDay = (day: keyof OperatingHours): keyof OperatingHours => {
    const index = DAYS.indexOf(day);
    return DAYS[(index + 1) % 7];
  };

  // -------------------------
  // Cross-Day Validation
  // -------------------------
  const getMinimumStartTime = (): string | undefined => {
    if (!selectedDay) return undefined;
    
    const prevDay = getPreviousDay(selectedDay);
    const prevSchedule = operatingHours[prevDay];
    
    console.log('=== PREVIOUS DAY CHECK ===');
    console.log('Current Day:', selectedDay);
    console.log('Previous Day:', prevDay);
    console.log('Previous Schedule:', prevSchedule);
    
    let minTime: string | undefined = undefined;
    
    // Check if previous day's first period extends into current day
    if (prevSchedule.firstHalf.start && prevSchedule.firstHalf.end) {
      if (isOvernightTime(prevSchedule.firstHalf.start, prevSchedule.firstHalf.end)) {
        minTime = prevSchedule.firstHalf.end;
        console.log('✅ Previous day first period is overnight, ends at:', minTime);
      } else {
        console.log('❌ Previous day first period is NOT overnight');
      }
    }
    
    // Check if previous day's second period extends into current day (might be later!)
    if (prevSchedule.secondHalf.start && prevSchedule.secondHalf.end) {
      if (isOvernightTime(prevSchedule.secondHalf.start, prevSchedule.secondHalf.end)) {
        if (!minTime || prevSchedule.secondHalf.end > minTime) {
          minTime = prevSchedule.secondHalf.end;
          console.log('✅ Previous day second period is overnight, ends at:', minTime);
        }
      } else {
        console.log('❌ Previous day second period is NOT overnight');
      }
    }
    
    console.log('Minimum Start Time:', minTime || 'No restriction');
    console.log('=========================\n');
    
    return minTime;
  };

  const checkPreviousDayOverlap = (): string | null => {
    if (!selectedDay || !editingSchedule) return null;
    
    const prevDay = getPreviousDay(selectedDay);
    const prevSchedule = operatingHours[prevDay];
    const currentStart = editingSchedule.firstHalf.start;
    
    if (!currentStart) return null;
    
    console.log('=== PREVIOUS DAY OVERLAP CHECK ===');
    console.log('Current Day:', selectedDay);
    console.log('Current Start:', currentStart);
    console.log('Previous Day:', prevDay);
    
    // Check if previous day's first period extends into current day
    if (prevSchedule.firstHalf.start && prevSchedule.firstHalf.end) {
      if (isOvernightTime(prevSchedule.firstHalf.start, prevSchedule.firstHalf.end)) {
        // Use <= to prevent overlap (no touching at boundary)
        if (currentStart <= prevSchedule.firstHalf.end) {
          const dayCapitalized = selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1);
          const prevDayCapitalized = prevDay.charAt(0).toUpperCase() + prevDay.slice(1);
          const error = `${dayCapitalized} cannot start at ${format12Hour(currentStart)} because ${prevDayCapitalized}'s overnight period ends at ${format12Hour(prevSchedule.firstHalf.end)} (${dayCapitalized}). ${dayCapitalized} must start AFTER ${format12Hour(prevSchedule.firstHalf.end)}.`;
          console.log('❌ ERROR:', error);
          console.log('===================================\n');
          return error;
        }
      }
    }
    
    // Check if previous day's second period extends into current day
    if (prevSchedule.secondHalf.start && prevSchedule.secondHalf.end) {
      if (isOvernightTime(prevSchedule.secondHalf.start, prevSchedule.secondHalf.end)) {
        // Use <= to prevent overlap (no touching at boundary)
        if (currentStart <= prevSchedule.secondHalf.end) {
          const dayCapitalized = selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1);
          const prevDayCapitalized = prevDay.charAt(0).toUpperCase() + prevDay.slice(1);
          const error = `${dayCapitalized} cannot start at ${format12Hour(currentStart)} because ${prevDayCapitalized}'s second overnight period ends at ${format12Hour(prevSchedule.secondHalf.end)} (${dayCapitalized}). ${dayCapitalized} must start AFTER ${format12Hour(prevSchedule.secondHalf.end)}.`;
          console.log('❌ ERROR:', error);
          console.log('===================================\n');
          return error;
        }
      }
    }
    
    console.log('✅ No overlap with previous day');
    console.log('===================================\n');
    return null;
  };

  const checkNextDayConflict = (): string[] => {
    if (!selectedDay || !editingSchedule) return [];
    
    const nextDay = getNextDay(selectedDay);
    const nextSchedule = operatingHours[nextDay];
    const warnings: string[] = [];
    
    console.log('=== NEXT DAY CONFLICT CHECK ===');
    console.log('Current Day:', selectedDay);
    console.log('Next Day:', nextDay);
    console.log('Current Schedule:', editingSchedule);
    console.log('Next Schedule:', nextSchedule);
    
    // Check if current day's first period extends into next day
    if (editingSchedule.firstHalf.start && editingSchedule.firstHalf.end) {
      if (isOvernightTime(editingSchedule.firstHalf.start, editingSchedule.firstHalf.end)) {
        console.log('✅ Current day first period is overnight');
        if (nextSchedule.firstHalf.start && nextSchedule.firstHalf.start < editingSchedule.firstHalf.end) {
          const warning = `${nextDay} starts at ${nextSchedule.firstHalf.start} but ${selectedDay} ends at ${editingSchedule.firstHalf.end}`;
          warnings.push(warning);
          console.log('⚠️  WARNING:', warning);
        }
      }
    }
    
    // Check if current day's second period extends into next day
    if (editingSchedule.secondHalf.start && editingSchedule.secondHalf.end) {
      if (isOvernightTime(editingSchedule.secondHalf.start, editingSchedule.secondHalf.end)) {
        console.log('✅ Current day second period is overnight');
        if (nextSchedule.firstHalf.start && nextSchedule.firstHalf.start < editingSchedule.secondHalf.end) {
          const warning = `${nextDay} starts at ${nextSchedule.firstHalf.start} but ${selectedDay}'s second period ends at ${editingSchedule.secondHalf.end}`;
          warnings.push(warning);
          console.log('⚠️  WARNING:', warning);
        }
      }
    }
    
    console.log('Total warnings:', warnings.length);
    console.log('===============================\n');
    
    return warnings;
  };

  // -------------------------
  // Handle day customization
  // -------------------------
  const handleDayCustomize = (day: keyof OperatingHours) => {
    if (selectedDay === day) {
      // Clicking same day - collapse
      setSelectedDay(null);
      setEditingSchedule(null);
      setHasSecondPeriod(false);
    } else {
      // Clicking different day - expand
      const daySchedule = operatingHours[day];
      setSelectedDay(day);
      setEditingSchedule({ ...daySchedule });
      setHasSecondPeriod(!!(daySchedule.secondHalf.start || daySchedule.secondHalf.end));
    }
  };

  const handleInlineFirstPeriodChange = (field: 'start' | 'end', value: string) => {
    if (!editingSchedule) return;
    
    console.log('=== FIRST PERIOD CHANGE ===');
    console.log('Day:', selectedDay);
    console.log('Field:', field);
    console.log('New Value:', value);
    console.log('Current Schedule:', editingSchedule);
    
    const newSchedule = {
      ...editingSchedule,
      firstHalf: {
        ...editingSchedule.firstHalf,
        [field]: value,
      },
    };

    // Clear second period if first period becomes 24-hour
    if (
      newSchedule.firstHalf.start &&
      newSchedule.firstHalf.end &&
      newSchedule.firstHalf.start === newSchedule.firstHalf.end
    ) {
      console.log('⚠️  24-hour operation detected - clearing second period');
      newSchedule.secondHalf = { start: '', end: '' };
      setHasSecondPeriod(false);
    }

    console.log('New Schedule:', newSchedule);
    console.log('Is Overnight:', isOvernightTime(newSchedule.firstHalf.start, newSchedule.firstHalf.end));
    console.log('===========================\n');

    setEditingSchedule(newSchedule);
  };

  const handleInlineSecondPeriodChange = (field: 'start' | 'end', value: string) => {
    if (!editingSchedule) return;
    
    console.log('=== SECOND PERIOD CHANGE ===');
    console.log('Day:', selectedDay);
    console.log('Field:', field);
    console.log('New Value:', value);
    console.log('First Period:', editingSchedule.firstHalf);
    console.log('Current Second Period:', editingSchedule.secondHalf);
    
    const newSchedule = {
      ...editingSchedule,
      secondHalf: {
        ...editingSchedule.secondHalf,
        [field]: value,
      },
    };
    
    console.log('New Second Period:', newSchedule.secondHalf);
    console.log('Is Second Overnight:', isOvernightTime(newSchedule.secondHalf.start, newSchedule.secondHalf.end));
    console.log('============================\n');
    
    setEditingSchedule(newSchedule);
  };

  const handleInlineToggleSecondPeriod = () => {
    if (!editingSchedule) return;
    
    if (hasSecondPeriod) {
      setEditingSchedule({
        ...editingSchedule,
        secondHalf: { start: '', end: '' },
      });
      setHasSecondPeriod(false);
    } else {
      setHasSecondPeriod(true);
    }
  };

  const handleInlineClosedToggle = () => {
    if (!editingSchedule) return;
    
    const newClosed = !editingSchedule.isClosed;
    setEditingSchedule({
      ...editingSchedule,
      isClosed: newClosed,
      firstHalf: newClosed ? { start: '', end: '' } : editingSchedule.firstHalf,
      secondHalf: newClosed ? { start: '', end: '' } : editingSchedule.secondHalf,
    });
    if (newClosed) {
      setHasSecondPeriod(false);
    }
  };

  const handleInlineSave = () => {
    if (!selectedDay || !editingSchedule) return;
    
    console.log('=== SAVE DAY SCHEDULE ===');
    console.log('Day:', selectedDay);
    console.log('Schedule:', editingSchedule);
    console.log('Is Valid:', isInlineValid);
    console.log('Field Errors:', getFieldErrors());
    console.log('Previous Day Overlap:', checkPreviousDayOverlap());
    console.log('Next Day Conflicts:', checkNextDayConflict());
    
    const newSchedule = {
      ...operatingHours,
      [selectedDay]: editingSchedule,
    };
    
    console.log('Full Operating Hours:', newSchedule);
    console.log('=========================\n');
    
    onChange(newSchedule);
    setSelectedDay(null);
    setEditingSchedule(null);
    setHasSecondPeriod(false);
  };

  const handleInlineCancel = () => {
    setSelectedDay(null);
    setEditingSchedule(null);
    setHasSecondPeriod(false);
  };

  // Get field-specific validation errors
  const getFieldErrors = () => {
    if (!editingSchedule || editingSchedule.isClosed) {
      return {
        firstStart: '',
        firstEnd: '',
        secondStart: '',
        secondEnd: '',
        overlap: '',
      };
    }

    const firstHalf = editingSchedule.firstHalf;
    const secondHalf = editingSchedule.secondHalf;
    const is24Hour = firstHalf.start && firstHalf.end && firstHalf.start === firstHalf.end;

    const errors = {
      firstStart: !firstHalf.start ? 'Start time is required' : '',
      firstEnd: !firstHalf.end ? 'End time is required' : '',
      secondStart: '',
      secondEnd: '',
      overlap: '',
    };

    if (hasSecondPeriod) {
      if (is24Hour) {
        errors.overlap = 'Cannot add second period for 24-hour operation.';
      } else {
        if (!secondHalf.start) errors.secondStart = 'Start time is required';
        if (!secondHalf.end) errors.secondEnd = 'End time is required';
        
        if (
          isValidTimeSlot(firstHalf.start, firstHalf.end) &&
          isValidTimeSlot(secondHalf.start, secondHalf.end) &&
          hasTimeOverlap(firstHalf.start, firstHalf.end, secondHalf.start, secondHalf.end)
        ) {
          errors.overlap = 'Periods overlap. Please adjust times.';
        }
      }
    }

    return errors;
  };

  const fieldErrors = getFieldErrors();
  const isInlineValid = !fieldErrors.firstStart && !fieldErrors.firstEnd && 
                        !fieldErrors.secondStart && !fieldErrors.secondEnd && 
                        !fieldErrors.overlap;
  const is24HourEditing = editingSchedule?.firstHalf.start && editingSchedule?.firstHalf.end && 
                          editingSchedule.firstHalf.start === editingSchedule.firstHalf.end;

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="border-2 border-gray-300 rounded-lg bg-white">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between border-b border-gray-200 rounded-t-lg"
      >
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-gray-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          </svg>
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-700">
              Customize
            </p>
            <p className="text-xs text-gray-600">
              Choose a particular day to edit and customize its operating time
            </p>
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="p-3">
          {/* Day Buttons */}
          <div className="flex gap-1.5 mb-3">
            {DAYS.map((day) => {
              const dayData = operatingHours[day];
              const hasSecondPeriodDay = !!(dayData.secondHalf.start || dayData.secondHalf.end);
              const dayLabel = day.charAt(0).toUpperCase() + day.slice(1, 3);
              const isSelected = selectedDay === day;
              
              return (
                <button
                  key={day}
                  onClick={() => handleDayCustomize(day)}
                  className={`
                    relative px-3 py-2 text-sm font-medium rounded transition-all
                    ${isSelected 
                      ? 'bg-[var(--accent-color)] text-white shadow-sm' 
                      : dayData.isClosed 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : hasSecondPeriodDay 
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                  title={dayData.isClosed ? 'Closed' : hasSecondPeriodDay ? '2 periods' : 'Single period'}
                >
                  {dayLabel}
                  {hasSecondPeriodDay && !isSelected && !dayData.isClosed && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full border border-white"></span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Inline Editor */}
          {selectedDay && editingSchedule && (
            <div className="pt-3 border-t border-gray-200">
              {/* Header with Close Button */}
              <div className="flex items-center justify-between mb-3">
                <h6 className="text-sm font-semibold text-gray-700">
                  {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}
                </h6>
                <button
                  onClick={handleInlineCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Close"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Closed Toggle */}
              <div className="mb-3">
                <ToggleSwitch
                  label="Mark as Closed"
                  isActive={editingSchedule.isClosed}
                  onClick={handleInlineClosedToggle}
                />
              </div>

              {!editingSchedule.isClosed && (
                <>
                  {/* First Period */}
                  <div className="mb-3">
                    <h6 className="text-sm font-semibold text-gray-700 mb-2">First Period</h6>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <TimeSlotSelector
                          label="Start Time"
                          value={editingSchedule.firstHalf.start}
                          onChange={(v) => handleInlineFirstPeriodChange('start', v)}
                          excludeBefore={getMinimumStartTime()}
                          id={`customize-${selectedDay}-first-start`}
                        />
                        {fieldErrors.firstStart && (
                          <p className="text-red-600 text-xs mt-1 flex items-center space-x-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>{fieldErrors.firstStart}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex-1">
                        <TimeSlotSelector
                          label="End Time"
                          value={editingSchedule.firstHalf.end}
                          onChange={(v) => handleInlineFirstPeriodChange('end', v)}
                          minTime={editingSchedule.firstHalf.start}
                          id={`customize-${selectedDay}-first-end`}
                        />
                        {editingSchedule.firstHalf.start && editingSchedule.firstHalf.end && 
                         isOvernightTime(editingSchedule.firstHalf.start, editingSchedule.firstHalf.end) && (
                          <p className="text-xs text-orange-700 mt-1">
                            (Next Day)
                          </p>
                        )}
                        {fieldErrors.firstEnd && (
                          <p className="text-red-600 text-xs mt-1 flex items-center space-x-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>{fieldErrors.firstEnd}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Second Period Toggle */}
                  {!is24HourEditing && (
                    <div className="mb-3">
                      <ToggleSwitch
                        label="Second Operating Period"
                        isActive={hasSecondPeriod}
                        onClick={handleInlineToggleSecondPeriod}
                      />
                    </div>
                  )}

                  {/* Overlap Error */}
                  {fieldErrors.overlap && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-start space-x-1">
                      <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <p className="text-xs text-red-700">{fieldErrors.overlap}</p>
                    </div>
                  )}

                  {/* Second Period */}
                  {hasSecondPeriod && !is24HourEditing && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <h6 className="text-sm font-semibold text-gray-700">Second Operating Period</h6>
                        <button
                          onClick={() => {
                            setEditingSchedule({
                              ...editingSchedule,
                              secondHalf: { start: '', end: '' },
                            });
                            setHasSecondPeriod(false);
                          }}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove second period"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <TimeSlotSelector
                            label="Start Time"
                            value={editingSchedule.secondHalf.start}
                            onChange={(v) => handleInlineSecondPeriodChange('start', v)}
                            minTime={editingSchedule.firstHalf.end}
                            maxTime={editingSchedule.firstHalf.start}
                            excludeBeforeMin={true}
                            id={`customize-${selectedDay}-second-start`}
                          />
                          {fieldErrors.secondStart && (
                            <p className="text-red-600 text-xs mt-1 flex items-center space-x-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              <span>{fieldErrors.secondStart}</span>
                            </p>
                          )}
                        </div>
                        <div className="flex-1">
                          <TimeSlotSelector
                            label="End Time"
                            value={editingSchedule.secondHalf.end}
                            onChange={(v) => handleInlineSecondPeriodChange('end', v)}
                            minTime={editingSchedule.secondHalf.start}
                            maxTime={editingSchedule.firstHalf.start}
                            excludeBeforeMin={true}
                            id={`customize-${selectedDay}-second-end`}
                          />
                          {editingSchedule.secondHalf.start && editingSchedule.secondHalf.end && 
                           isOvernightTime(editingSchedule.secondHalf.start, editingSchedule.secondHalf.end) && (
                            <p className="text-xs text-orange-700 mt-1">
                              (Next Day)
                            </p>
                          )}
                          {fieldErrors.secondEnd && (
                            <p className="text-red-600 text-xs mt-1 flex items-center space-x-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              <span>{fieldErrors.secondEnd}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
                <button
                  onClick={handleInlineCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInlineSave}
                  disabled={!isInlineValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--accent-color)] rounded hover:bg-[var(--accent-color)]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
